import type {
  ExecutionsHistorySectionRequest,
  ExecutionsHistorySectionResponse,
  ReportEventDrilldownRequest,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { isInFlightLimitError } from '@src/util/cloud-functions/callableDomainCode';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

const MAX_CONCURRENT_REQUESTS = 2;
const MAX_ATTEMPTS = 3;
const FIRST_RETRY_DELAY_MS = 500;
const RETRY_DELAY_STEP_MS = 500;
const RETRY_JITTER_MS = 250;

let activeRequests = 0;
const queuedRequests: Array<() => void> = [];

const acquireRequestSlot = async (): Promise<void> => {
  if (activeRequests < MAX_CONCURRENT_REQUESTS) {
    activeRequests += 1;
    return;
  }

  await new Promise<void>(resolve => queuedRequests.push(resolve));
};

const releaseRequestSlot = (): void => {
  const nextRequest = queuedRequests.shift();
  if (nextRequest) {
    nextRequest();
    return;
  }

  activeRequests -= 1;
};

const runWithRequestSlot = async <T>(request: () => Promise<T>): Promise<T> => {
  await acquireRequestSlot();
  try {
    return await request();
  } finally {
    releaseRequestSlot();
  }
};

const wait = (durationMs: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, durationMs));

const requestWithRetry = async <T>(request: () => Promise<T>): Promise<T> => {
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    try {
      return await runWithRequestSlot(request);
    } catch (error) {
      if (!isInFlightLimitError(error) || attempt === MAX_ATTEMPTS - 1) throw error;

      const delayMs = FIRST_RETRY_DELAY_MS + attempt * RETRY_DELAY_STEP_MS + Math.random() * RETRY_JITTER_MS;
      await wait(delayMs);
    }
  }

  throw new Error('Executions history request exhausted its retry attempts');
};

class ExecutionsHistoryDashboardRepository {
  async getSection<TData = unknown>(
    request: ExecutionsHistorySectionRequest | ReportEventDrilldownRequest,
  ): Promise<ExecutionsHistorySectionResponse<TData>> {
    const callSection = apiCallable<ExecutionsHistorySectionRequest | ReportEventDrilldownRequest, ExecutionsHistorySectionResponse<TData>>(
      API_OPERATIONS.EXECUTIONS_HISTORY_DASHBOARD,
    );
    return requestWithRetry(async () => {
      const response = await callSection(request);
      return response.data;
    });
  }
}

export default ExecutionsHistoryDashboardRepository;
